import type { HTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'
import './card.css'

// `title` is deliberately taken over from the HTML attribute of the same name: a card title is
// rendered content, not a tooltip string. Anything wanting the native tooltip can use
// `aria-describedby` or wrap the card.
export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Mono uppercase eyebrow above the title. */
  kicker?: ReactNode
  title?: ReactNode
  /** Elevation is a whisper here — cards are bordered by default and rarely shadowed. */
  elevation?: 'none' | 'sm' | 'md' | 'lg'
  /** The 1px accent top rule used by the dashboard's domain-score cards. */
  accentTop?: boolean
  /**
   * The title's element. A HEADING by default: rendered as a plain div, Settings, Plans and every
   * finding page had no headings at all, so a screen-reader user could not jump between sections.
   */
  titleAs?: 'h2' | 'h3' | 'div'
  children?: ReactNode
}

export function Card({
  kicker,
  title,
  elevation = 'none',
  accentTop = false,
  titleAs = 'h2',
  className,
  children,
  ...rest
}: CardProps) {
  const TitleTag = titleAs
  return (
    <div
      className={cx(
        'card',
        elevation !== 'none' && `elev-${elevation}`,
        accentTop && 'card-accent-top',
        className,
      )}
      {...rest}
    >
      {kicker ? <div className="card-kicker">{kicker}</div> : null}
      {title ? <TitleTag className="card-title">{title}</TitleTag> : null}
      {children}
    </div>
  )
}

export function CardBody({ className, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cx('card-body', className)} {...rest} />
}

export function CardMeta({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('card-meta', className)} {...rest} />
}
